import React, { useEffect, useRef, useState } from 'react';

const StateDropdown = ({ options, value, onChange }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedValue, setSelectedValue] = useState(value);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const selectRef = useRef(null);
    const optionsRef = useRef([]);
    const [currentQuery, setCurrentQuery] = useState('');
    const previousTimeRef = useRef(Date.now());

    const handleOptionClick = (e, value) => {
        e.stopPropagation();
        setSelectedValue(value);
        setShowDropdown(false);
        setHighlightedIndex(-1); // Reset highlighted index
        if (onChange) {
            onChange(value);
        }
    };

    const handleShowDropdown = () => {
        setShowDropdown(prev => !prev);
        setHighlightedIndex(-1); // Reset highlighted index when dropdown opens
    };

    const handleOutsideClick = (event) => {
        if (selectRef.current && !selectRef.current.contains(event.target)) {
            setShowDropdown(false);
        }
    };

    const handleKeyDown = (event) => {
        if (!showDropdown) return;

        if (event.key === 'ArrowDown') {
            setHighlightedIndex(prevIndex => {
                const newIndex = Math.min(prevIndex + 1, options.length - 1);
                scrollToOption(newIndex);
                return newIndex;
            });
        } else if (event.key === 'ArrowUp') {
            setHighlightedIndex(prevIndex => {
                const newIndex = Math.max(prevIndex - 1, 0);
                scrollToOption(newIndex);
                return newIndex;
            });
        } else if (event.key === 'Enter' && highlightedIndex >= 0) {
            handleOptionClick(event, options[highlightedIndex].value);
        } else {
            const currentTime = Date.now();
            if (currentTime - previousTimeRef.current > 500) {
                setCurrentQuery('');
            }
            setCurrentQuery(prevQuery => prevQuery + event.key.toLowerCase());
            previousTimeRef.current = currentTime;
        }
    };

    const scrollToOption = (index) => {
        if (optionsRef.current[index]) {
            optionsRef.current[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const getLabel = (value) => {
        const selectedItem = options.find((item) => item?.value === value);
        return selectedItem?.label;
    }

    useEffect(() => {
        const length = currentQuery.length;
        const index = options.findIndex((item) => item?.label.substr(0, length).toLowerCase().includes(currentQuery));
        setHighlightedIndex(index);
        scrollToOption(index)
    }, [currentQuery])

    useEffect(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        setSelectedValue(value);
        const index = options.findIndex((item) => item?.value === value);
        setHighlightedIndex(index);
        scrollToOption(index)
    }, [value, options, showDropdown]);

    return (
        <div ref={selectRef} className={`state-select-wrapper ${showDropdown ? 'show-dropdown' : ''}`}>
            <div className="state-select">
                <div className="state-select-trigger" onClick={handleShowDropdown} onKeyDown={handleKeyDown} tabIndex="0">
                    {
                        getLabel(value) ?? 'Select State'
                    }
                    
                    <span className='icon-updown-ico'></span>
                </div>
                {showDropdown && (
                    <div className="state-options">
                        {options.map((item, index) => (
                            <div
                                key={index}
                                ref={el => optionsRef.current[index] = el}
                                className={`state-option ${item.value === selectedValue || highlightedIndex === index ? 'state-option-selected' : ''}`}
                                onClick={(e) => handleOptionClick(e, item.value)}
                            >
                                {item.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StateDropdown;